import moment from 'moment';
import { first, isEmpty } from 'lodash/fp';
import ChartScoresService, {
    IGetDimensionsRiskQuery,
} from '../../services/ChartScoresService';
import APIService, {
    E_RISK_TYPE,
    IAPIMaxImpactMap,
    IExportChartConfig,
    IGetScoreDeltaQueryParams,
} from '../../services/APIService';
import { E_CHART_GROUP_IDENTIFIERS, E_DOMAIN_Y_SCALE, IChartData } from '../GQChart/chartInterfaces';
import ChartData from '../GQChart/ChartData';
import { COMPOSITE_DATA_INTERVAL } from '../GQChart/ChartStore';
import { VIEW } from '../../interfaces';
import { C_SCORES_DATE_FORMAT, TIME_WINDOW_PRESETS } from '../../constants';
import CustomWeightStore from 'stores/CustomWeightStore';
import { IPreloadedHeatmapApiData, IPreloadedMapApiData } from './exportInterfaces';
import { isContingentMode, isPulseChart, isRiskChart, isSortMode } from './templateUtils';
import { getMonthlyScoreDeltaDates } from '../../utils/scoreDeltaDates';

const getPoliticalRiskQuery = (
    chartConfig: IExportChartConfig
): IGetDimensionsRiskQuery => {
    const from = moment(chartConfig.startDate);
    const until = moment(chartConfig.endDate);
    const virtualToday = moment(chartConfig.virtualToday);

    const isDimensionBased = chartConfig.viewType === VIEW.COUNTRY_GRAPH;

    return {
        from,
        until,
        countries: isDimensionBased
            ? [chartConfig.countries[0]].filter(Boolean)
            : chartConfig.countries,
        identifiers: chartConfig.riskIds,
        identifierType: chartConfig.riskType,
        isDimensionBased,
        projectionDate: chartConfig.projectionDataOn ? virtualToday : null,
        geoquantLine: isRiskChart(chartConfig.riskChartMode),
        showPulseData: isPulseChart(chartConfig.riskChartMode) &&
            chartConfig.frequency !==
                COMPOSITE_DATA_INTERVAL.MONTHLY,
        isMonthly: chartConfig.frequency === COMPOSITE_DATA_INTERVAL.MONTHLY,
        isExportChartMode: true,
    };
};

export const loadCompositeRiskData = async (
    chartConfig: IExportChartConfig
): Promise<IChartData[]> => {
    if (isEmpty(chartConfig.riskIds) || isEmpty(chartConfig.countries)) {
        return [];
    }

    const query = getPoliticalRiskQuery(chartConfig);

    if (isEmpty(query.countries) || isEmpty(query.identifiers)) {
        return [];
    }

    return ChartScoresService.getPoliticalRiskChart(query);
};

export const loadContingentRiskData = async (
    chartConfig: IExportChartConfig
): Promise<IChartData[]> => {
    if (isEmpty(chartConfig.riskIds) || isEmpty(chartConfig.countries)) {
        return [];
    }

    const riskId = first(chartConfig.riskIds);
    const baseCountryId = first(chartConfig.countries);
    const contingentCountriesIds = chartConfig.countries.slice(1);

    if (!riskId || !baseCountryId || isEmpty(contingentCountriesIds)) {
        return [];
    }

    const risks = await APIService.getContingentRisk(riskId, baseCountryId);

    const countryMaxImpactMaps: { [countryId: number]: IAPIMaxImpactMap } = {};

    await Promise.all(
        contingentCountriesIds.map(countryId =>
            APIService.getMaxImpactMaps(
                E_RISK_TYPE.CONTINGENT,
                riskId,
                [baseCountryId, countryId]
            )
                .then(map => {
                    countryMaxImpactMaps[countryId] = map;
                })
                .catch(() => {
                    countryMaxImpactMaps[countryId] = {} as IAPIMaxImpactMap;
                })
        )
    );

    const contingentChartLines = contingentCountriesIds
        .filter(currentCountryId => !!risks[currentCountryId])
        .map(currentCountryId => {
            const targetRiskData = risks[currentCountryId];
            const targetMaxImpactMap = countryMaxImpactMaps[currentCountryId];

            return ChartScoresService.generateContingentRiskLine(
                currentCountryId,
                targetRiskData,
                targetMaxImpactMap
            );
        });

    const contingentRiskCharts =
        await ChartScoresService.getContingentRiskCharts(
            contingentChartLines,
            getPoliticalRiskQuery(chartConfig)
        );

    return contingentRiskCharts as ChartData[];
};

export const loadCurrencyData = async (
    chartConfig: IExportChartConfig
): Promise<IChartData | null> => {
    if (!chartConfig.currencyOn || isEmpty(chartConfig.currencyCountry)) {
        return null;
    }

    const linesData = await ChartScoresService.getCurrencyExchange({
        startDate: moment(chartConfig.startDate),
        endDate: moment(chartConfig.endDate),
        countries: chartConfig.currencyCountry,
    });

    const chart = new ChartData(
            linesData,
            {
                expanded: E_DOMAIN_Y_SCALE.standalone,
                minimized: E_DOMAIN_Y_SCALE.standalone,
            },
            E_CHART_GROUP_IDENTIFIERS.CURRENCY_EXCHANGE,
            moment(chartConfig.startDate).clone().startOf('day'),
            moment(chartConfig.endDate).clone().endOf('day')
        );
    return chart
};

export const loadGlobalIndexData = async (
    chartConfig: IExportChartConfig
): Promise<IChartData | null> => {
    if (!chartConfig.globalIndexTypes?.length) {
        return null;
    }

    return ChartScoresService.getGlobalIndexChart(
        chartConfig.globalIndexTypes,
        moment(chartConfig.startDate),
        moment(chartConfig.endDate),
        first(chartConfig.macroeconomicsCountry)
    );
};

export const preloadChartData = async (
    chartConfig: IExportChartConfig
): Promise<IChartData[]> => {
    const data: IChartData[] = [];
    const isContingentView = chartConfig.viewType === VIEW.CONTINGENT_GRAPH;

    const riskData = isContingentView
        ? await loadContingentRiskData(chartConfig)
        : await loadCompositeRiskData(chartConfig);

    data.push(...riskData);

    const currencyData = await loadCurrencyData(chartConfig);
    if (currencyData) {
        data.push(currencyData);
    }

    const globalIndexData = await loadGlobalIndexData(chartConfig);
    if (globalIndexData) {
        data.push(globalIndexData);
    }

    return data;
};

export const preloadHeatmapData = async (
    chartConfig: IExportChartConfig
): Promise<IPreloadedHeatmapApiData> => {
    const riskType = chartConfig.riskType;
    const heatmap = chartConfig.heatmap;
    const visibleCountryIds = heatmap.countriesList;
    const scoreType = heatmap.scoreType;
    const minDate = heatmap.startDate;
    const numDays = TIME_WINDOW_PRESETS[heatmap.timeSpan].val;
    const startDate = moment(minDate || moment()).format(C_SCORES_DATE_FORMAT);

    const [indicators, dimensionsTree, activeCountries, percentilesData] =
        await Promise.all([
            APIService.getActiveClientFacingIndicators(),
            APIService.getActiveDimensionsTree(),
            APIService.getActiveCountries(),
            APIService.getHeatmap(
                riskType,
                scoreType,
                startDate,
                numDays,
                visibleCountryIds
            ),
        ]);

    return {
        indicators,
        dimensionsTree,
        activeCountries,
        percentilesData,
    };
};

export const preloadMapData = async (
    chartConfig: IExportChartConfig
): Promise<IPreloadedMapApiData> => {
    const isContingent = isContingentMode(chartConfig);
    const isGQ = isContingent ? true : !CustomWeightStore.hasActiveCustomWeight;

    const rawDates = {
        virtualToday: moment(chartConfig.virtualToday),
        dateStart: moment(chartConfig.startDate),
        dateEnd: moment(chartConfig.endDate),
    };
    const isMonthly =
        chartConfig.frequency === COMPOSITE_DATA_INTERVAL.MONTHLY &&
        isSortMode(chartConfig);
    const { virtualToday, dateStart, dateEnd } = isMonthly
        ? getMonthlyScoreDeltaDates(rawDates)
        : rawDates;

    const params: IGetScoreDeltaQueryParams = {
        riskType: chartConfig.riskType,
        riskId: first(chartConfig.riskIds),
        countryId: first(chartConfig.countries || []),
        virtualToday,
        dateStart,
        dateEnd,
    };

    const [groups, scoreDeltas, geoData] = await Promise.all([
        APIService.getActiveGroups(),
        APIService.getScoreDelta(params, isGQ),
        APIService.getActiveCountries(),
    ]);

    return {
        groups,
        scoreDeltas,
        geoData,
    };
};
